import React, { useState } from 'react'
import { formatDateTime } from '@/utils/dateFormat'
import { useNotifications } from '@/hooks/useNotifications'
import { PageHeader } from '@/components/ui/PageHeader'
import { useNavigate } from 'react-router-dom'
import type { Notification as LabNotification } from '@/types/index'

const Notifications: React.FC = () => {
  const navigate = useNavigate()
  const { notifications, markRead, markAllRead } = useNotifications()
  const [activeTab, setActiveTab] = useState<'all' | 'unread'>('all')

  const filteredNotifications = activeTab === 'unread' ? notifications.filter((n) => !n.isRead) : notifications

  const handleNotificationClick = async (notification: LabNotification) => {
    if (!notification.isRead) {
      await markRead(notification.id)
    }
    if (notification.linkUrl) {
      navigate(notification.linkUrl)
    }
  }

  const getAppColor = (appName?: string) => {
    const colors: Record<string, string> = {
      Users: '#3b82f6',
      Companies: '#8b5cf6',
      Projects: '#ec4899',
      System: '#6b7280',
    }
    return colors[appName || 'System'] || '#6b7280'
  }

  return (
    <div style={{ padding: '0' }}>
      <PageHeader
        title="Notifications"
        actions={
          filteredNotifications.length > 0 ? (
            <button
              onClick={markAllRead}
              style={{
                padding: '8px 16px',
                backgroundColor: 'transparent',
                color: 'var(--color-magenta)',
                border: '1px solid var(--color-magenta)',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                fontFamily: 'var(--font-family)',
              }}
            >
              Mark all as read
            </button>
          ) : undefined
        }
      />

      <div style={{ padding: '24px' }}>
        {/* Tab navigation */}
        <div style={{ borderBottom: '1px solid var(--color-light-grey)', marginBottom: '24px' }}>
          <div style={{ display: 'flex', gap: '24px' }}>
            {(['all', 'unread'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                style={{
                  padding: '12px 0',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: activeTab === tab ? 'var(--color-magenta)' : 'var(--color-dark-grey)',
                  borderBottom: activeTab === tab ? '2px solid var(--color-magenta)' : 'none',
                  fontFamily: 'var(--font-family)',
                  textTransform: 'capitalize',
                }}
              >
                {tab === 'unread' ? `Unread (${notifications.filter((n) => !n.isRead).length})` : 'All'}
              </button>
            ))}
          </div>
        </div>

        {/* Notifications list */}
        {filteredNotifications.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--color-dark-grey)' }}>
            <p style={{ fontSize: '16px', margin: 0 }}>No notifications</p>
            <p style={{ fontSize: '14px', margin: '8px 0 0 0', color: 'var(--color-dark-grey)' }}>
              {activeTab === 'unread' ? 'You are all caught up' : 'No notifications yet'}
            </p>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '12px' }}>
            {filteredNotifications.map((notification) => (
              <div
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                style={{
                  padding: '16px',
                  backgroundColor: notification.isRead ? 'var(--color-white)' : '#faf8fb',
                  border: '1px solid var(--color-light-grey)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  borderLeft: notification.isRead ? '4px solid var(--color-light-grey)' : '4px solid var(--color-magenta)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = 'none'
                }}
              >
                <div style={{ display: 'flex', gap: '12px' }}>
                  {notification.sourceApp && (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '32px',
                        height: '32px',
                        borderRadius: '6px',
                        backgroundColor: getAppColor(notification.sourceApp),
                        color: 'var(--color-white)',
                        fontSize: '12px',
                        fontWeight: 600,
                        flexShrink: 0,
                      }}
                    >
                      {notification.sourceApp[0]}
                    </div>
                  )}

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        gap: '12px',
                        marginBottom: '4px',
                      }}
                    >
                      <div>
                        <h3 style={{ fontSize: '14px', fontWeight: 600, margin: 0, color: 'var(--color-black)' }}>
                          {notification.title}
                        </h3>
                        {notification.sourceApp && (
                          <span style={{ fontSize: '12px', color: 'var(--color-dark-grey)' }}>
                            from {notification.sourceApp}
                          </span>
                        )}
                      </div>
                      {!notification.isRead && (
                        <div
                          style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: 'var(--color-magenta)',
                            flexShrink: 0,
                            marginTop: '4px',
                          }}
                        />
                      )}
                    </div>

                    <p style={{ fontSize: '13px', color: 'var(--color-dark-grey)', margin: '0 0 8px 0', lineHeight: '1.4' }}>
                      {notification.message}
                    </p>

                    <div style={{ fontSize: '12px', color: 'var(--color-dark-grey)' }}>
                      {formatDateTime(notification.createdAt)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default Notifications
